import dayjs from 'dayjs';
import { startCase, capitalize } from 'lodash';
import { useShowSubscriber } from 'modules/for-verification/hooks';
import Button from 'partial/components/Button';
import NavTab, { NavTabItem } from 'partial/components/NavTab';
import SwitchToggle from 'partial/components/SwitchToggle';
import ThumbIcon from 'partial/components/ThumbIcon';
import React from 'react';
import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import cn from 'classnames';
import { PencilSimple } from '@phosphor-icons/react';
import { aclHelper, formatNumber, parseNumber } from 'helper';
import { useShowOrgWallet, useShowSubscriberSubscription } from '../hooks';
import { useOpenBanAccount } from '../modals/BanAccount';
import { useOpenBanExempt } from '../modals/BanExempt';
import Dashboard from './Dashboard';
import Integration from './Integration';
import MaskNames from './MaskNames';
import Messages from './Messages';
import History from './History';
import { useOpenEditRate } from '../modals/EditRate';
// TEMPORARILY DISABLED DUE TO REQUIREMENTS: Domain tab
// import Domain from './Domain';
import BanLogs from './BanLogs';

const tabRoutes = [
  {
    id: 1,
    to: 'dashboard',
    component: Dashboard,
  },
  {
    id: 2,
    to: 'mask-names',
    component: MaskNames,
  },
  {
    id: 3,
    to: 'integration',
    component: Integration,
  },
  {
    id: 4,
    to: 'messages',
    component: Messages,
  },
  {
    id: 5,
    to: 'history',
    component: History,
  },
  // TEMPORARILY DISABLED DUE TO REQUIREMENTS: Domain tab
  // {
  //   id: 6,
  //   to: 'domain',
  //   component: Domain,
  // },
  {
    id: 7,
    to: 'ban-logs',
    component: BanLogs,
  },
];

const IS_HIDDEN = true; // HIDDEN

function SubscriberProfile() {
  const { id } = useParams();
  const openEditRate = useOpenEditRate();
  const [isSubLoading, subscriber] = useShowSubscriber(id);
  const [isPlanLoading, subscription] = useShowSubscriberSubscription(id);
  const org = subscriber?.organization;
  const orgName = org?.name;
  const [isWalletLoading, wallet] = useShowOrgWallet(org?.uuid);
  const openBanExempt = useOpenBanExempt();
  const openBanAccount = useOpenBanAccount();
  return (
    <div className="flex-1 flex flex-col overflow-x-auto min-w-0">
      <div className="p-6 border-b border-neutral-200">
        {isSubLoading || isPlanLoading || isWalletLoading ? (
          <div className="animate-pulse flex space-x-4">
            <div className="rounded-full bg-neutral-100 h-24 w-24" />
            <div className="flex-1 space-y-6 py-1">
              <div className="h-8 bg-neutral-100 rounded" />
              <div className="space-y-3">
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-neutral-100 rounded col-span-2" />
                  <div className="h-8 bg-neutral-100 rounded col-span-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-neutral-100 rounded col-span-2" />
                  <div className="h-8 bg-neutral-100 rounded col-span-1" />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="h-8 bg-neutral-100 rounded col-span-2" />
                  <div className="h-8 bg-neutral-100 rounded col-span-1" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex md:flex-row flex-col items-center md:items-start gap-4 xl:gap-10">
            <ThumbIcon
              className="h-[52px] w-[52px] rounded-full"
              initialsClassName="!text-lg"
              alt={orgName}
              url={subscriber?.profile?.avatar || ''}
            />
            <div className="flex flex-col flex-1">
              <div className="pb-4 flex justify-between items-start border-b border-neutral-200">
                <div className="space-y-1 xl:space-y-0">
                  <h3 className="xl:hidden text-xl font-bold text-neutral-900">
                    {orgName}
                  </h3>
                  <div className="hidden xl:flex items-center gap-2">
                    <h3 className="text-xl font-bold text-neutral-900">
                      {orgName}
                    </h3>

                    {subscriber && (
                      <SwitchToggle
                        value={!subscriber?.organization?.is_banned}
                        label={
                          subscriber?.organization?.is_banned
                            ? 'Banned'
                            : 'Active'
                        }
                      />
                    )}
                  </div>
                  {subscription && !IS_HIDDEN && (
                    <>
                      <div className="text-sm text-neutral-500">
                        {capitalize(subscription?.name)} Account
                      </div>
                      <div className="flex gap-2 text-sm items-center mt-1">
                        <span className="text-neutral-500">Rate:</span>
                        <span className="font-medium text-neutral-900 tabular-nums">
                          {org?.rate_per_sms}
                        </span>
                        {aclHelper(
                          'subscriber_subscription',
                          'u',
                          <Button
                            link
                            size="sm"
                            icon={PencilSimple}
                            onClick={() => openEditRate(org)}
                          >
                            Edit
                          </Button>,
                        )}
                      </div>
                    </>
                  )}

                  <div className="xl:hidden w-min">
                    {subscriber && (
                      <SwitchToggle
                        value={!subscriber?.organization?.is_banned}
                        label={
                          subscriber?.organization?.is_banned
                            ? 'Banned'
                            : 'Active'
                        }
                      />
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <h2 className="text-3xl font-bold tracking-[-.02em] tabular-nums text-neutral-900">
                    {Boolean(wallet?.balance) && Boolean(org?.rate_per_sms)
                      ? formatNumber(
                          parseNumber(wallet?.balance) /
                            parseNumber(org?.rate_per_sms),
                          0,
                        )
                      : '--'}
                  </h2>
                  <div className="text-xs font-semibold uppercase tracking-[.05em] text-neutral-500">
                    SMS
                  </div>
                </div>
              </div>
              <div className="flex justify-between flex-col xl:flex-row gap-3 py-3">
                <div className="flex-grow grid grid-cols-2 gap-3 ">
                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-sm text-neutral-500">
                      Contact Person:
                    </h4>
                    <p className="text-sm font-medium text-neutral-900">
                      {startCase(
                        capitalize(
                          `${subscriber?.profile?.first_name || ''} ${
                            subscriber?.profile?.last_name || ''
                          }`,
                        ),
                      )}
                    </p>
                  </div>

                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-sm text-neutral-500">Mobile Number:</h4>
                    <p className="text-sm font-medium text-neutral-900">
                      {subscriber?.profile?.mobile_number}
                    </p>
                  </div>

                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-sm text-neutral-500 whitespace-nowrap">
                      Email Address:
                    </h4>
                    <p className="text-sm font-medium text-neutral-900 break-all">
                      {subscriber?.email}
                    </p>
                  </div>

                  <div className="flex flex-col 2xl:flex-row 2xl:gap-1">
                    <h4 className="text-sm text-neutral-500">
                      Registration Date:
                    </h4>
                    <p className="text-sm font-medium text-neutral-900">
                      {dayjs(subscriber?.profile?.created_at).format(
                        'MMM DD, YYYY',
                      )}
                    </p>
                  </div>
                </div>
                {aclHelper(
                  'subscriber_organization',
                  'u',
                  <div className="w-full xl:w-auto justify-center md:justify-start self-start grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-1 gap-3 xl:gap-2">
                    <Button
                      {...(org?.is_ban_exempted
                        ? { secondary: true }
                        : { outline: true })}
                      className="w-[160px]"
                      onClick={() => openBanExempt(subscriber)}
                    >
                      {org?.is_ban_exempted ? 'Exempted' : 'Ban exempt'}
                    </Button>
                    <Button
                      {...(org?.is_banned
                        ? { primary: true }
                        : { danger: true })}
                      className={cn(
                        'w-[160px] transition-opacity',
                        org?.is_ban_exempted ? 'opacity-0' : 'opacity-100',
                      )}
                      onClick={() => openBanAccount(subscriber)}
                    >
                      {org?.is_banned ? 'Unban account' : 'Ban account'}
                    </Button>
                  </div>,
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="flex-1 flex flex-col">
        <NavTab>
          {tabRoutes.map(({ id: identifier, to }) => (
            <NavTabItem key={identifier} to={to} label={startCase(to)} />
          ))}
        </NavTab>
        <div className="flex-1 h-full w-full overflow-auto">
          <div className="h-full px-4 pt-4 pb-2">
            <Routes>
              {tabRoutes.map(({ id: identifier, to, component: Component }) => (
                <Route key={identifier} path={to} element={<Component />} />
              ))}
              <Route index element={<Navigate replace to="dashboard" />} />
            </Routes>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubscriberProfile;
